<template>
    <section class="regulation-requirement-item">
        <div class="regulation-requirement-item__number">{{ number }}.</div>
        <div class="regulation-requirement-item__content">
            <div
                class="regulation-requirement-item__header"
                :class="{ 'regulation-requirement-item__header--clickable': collapsible }"
                :role="collapsible ? 'button' : null"
                :tabindex="collapsible ? 0 : null"
                :aria-controls="collapsible ? collapseId : null"
                :aria-expanded="collapsible ? (expanded ? 'true' : 'false') : null"
                @click="toggleRequirement"
                @keydown.enter.self.prevent="toggleRequirement"
                @keydown.space.self.prevent="toggleRequirement"
            >
                <div>
                    <div class="d-flex align-items-center flex-wrap">
                        <h5 class="mb-0 mr-1">{{ requirement.list.title }}</h5>
                        <span
                            :id="statusExplanationId"
                            :class="hasBlockingReasons
                                ? 'regulation-requirement-item__status-with-explanation'
                                : null"
                            :role="hasBlockingReasons ? 'button' : null"
                            :tabindex="hasBlockingReasons ? 0 : null"
                            :aria-label="hasBlockingReasons ? statusExplanationLabel : null"
                            @click="stopStatusToggle"
                        >
                            <b-badge :variant="statusVariant">{{ statusLabel }}</b-badge>
                            <feather-icon v-if="hasBlockingReasons" icon="InfoIcon" size="14" />
                        </span>
                        <b-popover
                            :target="statusExplanationId"
                            :disabled="!hasBlockingReasons"
                            triggers="hover click blur"
                            placement="bottom"
                        >
                            <template #title>{{ statusExplanationTitle }}</template>
                            <ul class="regulation-requirement-item__reasons mb-0 pl-1">
                                <li v-for="reason in blockingReasons" :key="reason.id">
                                    {{ reasonMessage(reason) }}
                                </li>
                            </ul>
                        </b-popover>
                    </div>
                    <div
                        v-if="legislationLabels.length > 0"
                        class="regulation-requirement-item__legislations"
                    >
                        {{ legislationLabels.join(', ') }}
                    </div>
                </div>
                <b-button
                    v-if="collapsible"
                    class="regulation-requirement-item__toggle btn-icon"
                    size="sm"
                    variant="flat-secondary"
                    :aria-controls="collapseId"
                    :aria-expanded="expanded ? 'true' : 'false'"
                    :aria-label="toggleLabel"
                    @click.stop="toggleRequirement"
                >
                    <feather-icon
                        :icon="expanded ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                    />
                </b-button>
            </div>

            <b-collapse :id="collapseId" :visible="!collapsible || expanded">
                <div v-if="showQuestion" class="regulation-requirement-item__questions">
                    <div
                        v-for="question in normalizedQuestions"
                        :key="question.id"
                        class="regulation-requirement-item__question"
                    >
                        <div>
                            <div
                                v-if="normalizedQuestions.length > 1"
                                class="regulation-requirement-item__question-list"
                            >
                                {{ question.list.title }}
                            </div>
                            <div
                                v-if="question.pathLabels.length > 0"
                                class="regulation-requirement-item__question-path text-less-muted"
                            >
                                {{ question.pathLabels.join(', ') }}
                            </div>
                            <div>{{ question.list.question }}</div>
                        </div>
                        <div class="regulation-requirement-item__answers">
                            <b-form-radio
                                :name="`requirement-question-${requirement.id}-${question.id}`"
                                :checked="question.state"
                                :value="true"
                                @change="answerQuestion(question, true)"
                            >
                                {{ $t('Component(Choice):yes') }}
                            </b-form-radio>
                            <b-form-radio
                                :name="`requirement-question-${requirement.id}-${question.id}`"
                                :checked="question.state"
                                :value="false"
                                @change="answerQuestion(question, false)"
                            >
                                {{ $t('Component(Choice):no') }}
                            </b-form-radio>
                        </div>
                    </div>
                </div>

                <div v-if="showScip" class="regulation-requirement-item__scip">
                    <b-form-group class="mb-0">
                        <template #label>
                            {{ translate('Component(Label):scip-number', 'SCIP number') }}
                            <span
                                v-if="showScipRequiredMarker"
                                class="text-danger ml-50"
                            >
                                {{ translate('Component(Text):required', 'Required') }}
                            </span>
                        </template>
                        <b-form-input
                            :value="scipNumber"
                            :state="scipRequired && scipNumber.trim().length === 0 ? false : null"
                            @input="$emit('scip-input', $event)"
                        />
                    </b-form-group>
                </div>

                <template v-if="canSelectSubstances">
                    <div
                        v-if="requirement.placements.length > 0"
                        class="regulation-requirement-item__substances"
                    >
                        <SubstanceRequirementSummary
                            v-for="placement in requirement.placements"
                            :key="placementKey(placement)"
                            :placement="placement"
                            :list="placement.list || requirement.list"
                            :property-config="propertyConfig"
                            :hide-candidate-list-categories="requirement.hideCandidateListCategories"
                            :editor-expanded="activeEditorKey === placementKey(placement)"
                            :information-required="placement.compliance
                                && placement.compliance.status !== 'complete'"
                            :show-requirement-status="showSubstanceRequirementStatus"
                            @toggle-editor="toggleEditor(placement)"
                            @change="emitPropertyChange"
                            @exclude="excludeSubstance(placement, $event)"
                        />
                    </div>
                    <div v-else class="text-muted mt-1">
                        {{ translate(
                            'Page(ReceivedRequest):Component(ComplianceInformation):no-substances-selected',
                            'No substances selected for this requirement.',
                        ) }}
                    </div>

                    <div class="mt-1">
                        <b-button variant="outline-primary" @click="openSelector">
                            <feather-icon icon="PlusIcon" class="mr-50" />
                            {{ translate(
                                'Page(ReceivedRequest):Component(ComplianceInformation):add-substance',
                                'Add substance',
                            ) }}
                        </b-button>
                    </div>
                </template>

                <CandidateListSafetyInstructions
                    v-if="showSafetyInstructions"
                    :instructions="context.complianceInformation.safetyInstructions"
                    @action="$emit('action', $event)"
                />

                <div
                    v-if="allQuestionsNegative"
                    class="regulation-requirement-item__negative-answer"
                >
                    {{ translate(
                        'Page(ReceivedRequest):Component(ComplianceInformation):no-relevant-substances',
                        'No relevant substances declared.',
                    ) }}
                </div>

            </b-collapse>
        </div>

        <SubstanceListSelectorModal
            ref="selector"
            :list="requirement.list"
            :legislations="requirement.legislations"
            :product="context.product"
            :product-statement-answers="context.product.productStatementAnswers || []"
            :included-substances="context.includedSubstances"
            :excluded-substances="context.excludedSubstances"
            :allowed-list-ids="selectorListIds"
            :show-questions="false"
            :show-no-single-parts-checkbox="true"
            :disable-automatic-question-state-inference="true"
            @include="(substance, listIds) => $emit('action', {
                type: 'substance.include',
                payload: { substance, substanceListIds: listIds },
            })"
            @exclude="$emit('action', { type: 'substance.exclude', payload: { substance: $event } })"
            @undefined="$emit('action', { type: 'substance.undefined', payload: { substance: $event } })"
            @closed="$emit('action', { type: 'selector.closed', payload: {} })"
        />
    </section>
</template>

<script>
import {
    BBadge,
    BButton,
    BCollapse,
    BFormGroup,
    BFormInput,
    BFormRadio,
    BPopover,
} from 'bootstrap-vue'
import { blockingReasons } from '../requirement-checks'
import CandidateListSafetyInstructions from './CandidateListSafetyInstructions.vue'
import SubstanceRequirementSummary from './SubstanceRequirementSummary.vue'
import SubstanceListSelectorModal from './SubstanceListSelectorModal.vue'

export default {
    name: 'RegulationRequirementItem',
    components: {
        BBadge,
        BButton,
        BCollapse,
        BFormGroup,
        BFormInput,
        BFormRadio,
        BPopover,
        CandidateListSafetyInstructions,
        SubstanceRequirementSummary,
        SubstanceListSelectorModal,
    },
    props: {
        number: { type: Number, required: true },
        requirement: { type: Object, required: true },
        context: { type: Object, required: true },
        propertyConfig: { type: Object, required: true },
        showScip: { type: Boolean, default: false },
        scipRequired: { type: Boolean, default: false },
        scipNumber: { type: String, default: '' },
        collapsible: { type: Boolean, default: false },
        expanded: { type: Boolean, default: true },
        showSubstanceRequirementStatus: { type: Boolean, default: true },
    },
    data() {
        return {
            activeEditorKey: null,
            knownPlacementKeys: [],
            placementsInitialized: false,
        }
    },
    computed: {
        showScipRequiredMarker() {
            return this.scipRequired && String(this.scipNumber || '').trim().length === 0
        },
        showSafetyInstructions() {
            const instructions = this.context.complianceInformation
                && this.context.complianceInformation.safetyInstructions
            const aboveThreshold = (this.requirement.facts || []).find(fact => (
                fact.id === 'candidate-list-substances-above-covered-weight'
            ))

            return Boolean(
                instructions
                && instructions.loaded
                && Number(aboveThreshold && aboveThreshold.value) > 0,
            )
        },
        showQuestion() {
            return Array.isArray(this.requirement.questions)
                ? this.requirement.questions.length > 0
                : this.requirement.list.hasQuestion()
        },
        normalizedQuestions() {
            if (Array.isArray(this.requirement.questions)) {
                return this.requirement.questions
            }
            if (!this.requirement.list.hasQuestion()) {
                return []
            }

            return [{
                id: String(this.requirement.list.id),
                list: this.requirement.list,
                state: this.requirement.questionState,
                pathLabels: [],
                branchListIds: [this.requirement.list.id],
            }]
        },
        selectorListIds() {
            if (Array.isArray(this.requirement.selectorListIds)) {
                return this.requirement.selectorListIds
            }

            return this.normalizedQuestions.length === 0
                || this.normalizedQuestions.some(question => question.state === true)
                ? [this.requirement.list.id]
                : []
        },
        canSelectSubstances() {
            return this.normalizedQuestions.length === 0
                || this.selectorListIds.length > 0
        },
        allQuestionsNegative() {
            return this.normalizedQuestions.length > 0
                && this.normalizedQuestions.every(question => question.state === false)
        },
        collapseId() {
            return `simple-compliance-requirement-${this._uid}`
        },
        toggleLabel() {
            return this.expanded
                ? this.translate('Component(Button):collapse', 'Collapse')
                : this.translate('Component(Button):expand', 'Expand')
        },
        blockingReasons() {
            return blockingReasons(this.requirement.checks)
        },
        blockingReasonSignature() {
            return this.blockingReasons.map(reason => reason.id).join('|')
        },
        hasBlockingReasons() {
            return this.blockingReasons.length > 0
        },
        statusExplanationId() {
            return `compliance-requirement-status-${this._uid}`
        },
        statusExplanationTitle() {
            return this.translate(
                'Page(ReceivedRequest):Component(ComplianceInformation):status-explanation-title',
                'Why is information required?',
            )
        },
        statusExplanationLabel() {
            return `${this.statusLabel}. ${this.statusExplanationTitle}`
        },
        statusVariant() {
            return this.requirement.status === 'complete' ? 'light-success' : 'light-warning'
        },
        statusLabel() {
            return this.requirement.status === 'complete'
                ? this.translate('Component(Status):complete', 'Complete')
                : this.translate('Component(Status):information-required', 'Information required')
        },
        legislationLabels() {
            return this.requirement.legislations
                .map(legislation => legislation.title || legislation.name || legislation.key)
                .filter(Boolean)
        },
    },
    watch: {
        'requirement.placements': {
            immediate: true,
            handler(placements) {
                const keys = placements.map(this.placementKey)
                const firstNewKey = keys.find(key => !this.knownPlacementKeys.includes(key))

                if (this.placementsInitialized && firstNewKey) {
                    this.activeEditorKey = firstNewKey
                }

                this.knownPlacementKeys = keys
                this.placementsInitialized = true
            },
        },
        blockingReasonSignature() {
            this.hideStatusExplanation()
        },
    },
    beforeDestroy() {
        this.hideStatusExplanation()
    },
    methods: {
        toggleRequirement() {
            if (this.collapsible) {
                this.$emit('toggle')
            }
        },
        stopStatusToggle(event) {
            if (this.hasBlockingReasons) {
                event.stopPropagation()
            }
        },
        translate(key, fallback) {
            const translation = this.$t(key)
            return translation && translation !== key ? translation : fallback
        },
        reasonMessage(reason) {
            return this.translate(reason.messageKey, reason.fallbackMessage)
        },
        hideStatusExplanation() {
            this.$root.$emit('bv::hide::popover', this.statusExplanationId)
        },
        placementKey(placement) {
            const substanceId = placement.container.substance && placement.container.substance.id
            const componentId = placement.component && placement.component.component
                ? placement.component.component.id
                : 'product'
            return `${componentId}-${substanceId}`
        },
        toggleEditor(placement) {
            const key = this.placementKey(placement)
            this.activeEditorKey = this.activeEditorKey === key ? null : key
        },
        excludeSubstance(placement, substance, callbacks = {}) {
            if (this.activeEditorKey === this.placementKey(placement)) {
                this.activeEditorKey = null
            }
            this.$emit('action', {
                type: 'substance.exclude',
                payload: { substance, save: true, ...callbacks },
            })
        },
        async answerQuestion(question, state) {
            if (!question || typeof question !== 'object' || !question.list) {
                state = question
                question = {
                    list: this.requirement.list,
                    branchListIds: [this.requirement.list.id],
                }
            }
            const affectedPlacements = this.requirement.placements.filter(placement => (
                question.branchListIds.some(listId => (
                    !Array.isArray(placement.container.lists)
                    || placement.container.lists.some(list => String(list.id) === String(listId))
                ))
            ))
            const affectedSubstances = affectedPlacements
                .map(({ container }) => container.substance)
                .filter(Boolean)
            if (state === false && !await this.confirmSubstanceRemoval(affectedPlacements)) {
                this.$nextTick(() => this.$forceUpdate())
                return
            }

            this.$emit('action', {
                type: 'list-question.update',
                payload: { list: question.list, state },
            })

            if (state === false && affectedSubstances.length > 0) {
                this.activeEditorKey = null
                this.$emit('action', {
                    type: 'substances.exclude',
                    payload: {
                        substances: affectedSubstances,
                        save: true,
                    },
                })
            }
        },
        async confirmSubstanceRemoval(placements) {
            if (
                placements.length === 0
                || !this.$bvModal
                || typeof this.$bvModal.msgBoxConfirm !== 'function'
            ) {
                return true
            }

            return this.$bvModal.msgBoxConfirm(
                this.translate(
                    'Page(ReceivedRequest):Component(ComplianceInformation):remove-substances-confirmation',
                    'The substances selected for this requirement will be removed. Continue?',
                ),
                {
                    title: this.translate(
                        'Page(ReceivedRequest):Component(ComplianceInformation):change-answer-confirmation-title',
                        'Change answer?',
                    ),
                    okTitle: this.translate('Component(Button):continue', 'Continue'),
                    cancelTitle: this.translate('Component(Button):cancel', 'Cancel'),
                    okVariant: 'danger',
                    cancelVariant: 'outline-secondary',
                    centered: true,
                },
            )
        },
        openSelector() {
            this.$refs.selector.show()
        },
        emitPropertyChange(substance, properties, callbacks = {}) {
            this.$emit('action', {
                type: 'substance-properties.change',
                payload: { substance, properties, ...callbacks },
            })
        },
    },
}
</script>

<style lang="scss" scoped>
.regulation-requirement-item {
    display: grid;
    gap: 1rem;
    grid-template-columns: 2rem minmax(0, 1fr);
    padding: 1.5rem 0;

    & + & {
        border-top: 1px solid #ebe9f1;
    }

    &__number {
        color: #5e5873;
        font-size: 1.1rem;
        font-weight: 600;
        padding-top: .15rem;
    }

    &__header,
    &__question {
        align-items: flex-start;
        display: flex;
        gap: 1rem;
        justify-content: space-between;
    }

    &__header--clickable {
        cursor: pointer;
        outline: none;

        &:focus-visible {
            border-radius: .357rem;
            box-shadow: 0 0 0 .2rem rgba(115, 103, 240, .25);
        }
    }

    &__questions {
        margin-top: 1rem;
    }

    &__question + &__question {
        border-top: 1px solid #ebe9f1;
        margin-top: .85rem;
        padding-top: .85rem;
    }

    &__question-list {
        color: #2f2c52;
        font-weight: 600;
        margin-bottom: .2rem;
    }

    &__question-path {
        font-size: .8rem;
        margin-bottom: .2rem;
    }

    &__legislations {
        color: #6e6b7b;
        font-size: .875rem;
        margin-top: .35rem;
    }

    &__status-with-explanation {
        align-items: center;
        color: #6e6b7b;
        cursor: help;
        display: inline-flex;
        gap: .35rem;
        outline: none;

        &:focus {
            color: #f58049;
        }
    }

    &__reasons {
        min-width: 15rem;

        li + li {
            margin-top: .35rem;
        }
    }

    &__answers {
        display: flex;
        flex: 0 0 auto;
        gap: 1.5rem;
    }

    &__substances {
        display: grid;
        gap: .75rem;
        margin-top: 1rem;
    }

    &__negative-answer {
        color: #28c76f;
        margin-top: 1rem;
    }

    &__scip {
        margin-top: 1rem;
        max-width: 34rem;
        padding-top: 1rem;
    }
}

@media (max-width: 767.98px) {
    .regulation-requirement-item {
        grid-template-columns: 1fr;

        &__number {
            display: none;
        }

        &__header,
        &__question {
            align-items: stretch;
            flex-direction: column;
        }
    }
}
</style>
